import type {ReactNode} from 'react';
import {useNavigate} from 'react-router-dom';
import {useMedia} from 'react-use';

import type {
  AvatarLabelProps,
  AvatarLayout,
} from '@/components/ui/avatar-label';
import {AvatarWithLabel} from '@/components/ui/avatar-label';
import {TooltipWrapper} from '@/components/ui/tooltip';
import {useAuthorize} from '@/hooks/useAuth';
import type {User} from '@/services/graphqlHooks';
import {cn} from '@/utils/ui';

export type UserAvatarProps = Pick<AvatarLabelProps, 'size' | 'className'> & {
  user: Pick<User, 'name' | 'avatar' | 'userName'>;
  layout?: AvatarLayout;
  noName?: boolean;
  nameAddon?: ReactNode;
  additionalInfo?: ReactNode;
};

const UserAvatar = ({
  user,
  size = 'default',
  layout = 'horizontal',
  noName,
  nameAddon,
  additionalInfo,
  className,
}: UserAvatarProps) => {
  const navigate = useNavigate();
  const canViewUser = useAuthorize('VIEW_USER');
  const isMobile = useMedia('(max-width: 767px)');

  const name = `${user.name.firstName} ${user.name.lastName}`;

  const handleClick = (evt: React.MouseEvent) => {
    if (!canViewUser) return;
    evt.stopPropagation();
    navigate(`/users/${user.userName}`);
  };

  const nameRow = nameAddon ? (
    <div className="flex items-center gap-1">
      {name}
      {nameAddon}
    </div>
  ) : (
    name
  );

  const label = noName ? undefined : additionalInfo ? (
    <div
      className={cn(
        'flex flex-col gap-0.5',
        layout === 'vertical' ? 'items-center' : 'items-start'
      )}
    >
      {nameRow}
      {additionalInfo}
    </div>
  ) : (
    nameRow
  );

  const avatarEl = (
    <div
      className={cn({'cursor-pointer': canViewUser}, className)}
      onClick={handleClick}
    >
      <AvatarWithLabel
        src={user.avatar || undefined}
        fallback={`${user.name.firstName[0]}${user.name.lastName[0]}`}
        label={label}
        color="secondary"
        size={size}
        layout={layout}
      />
    </div>
  );

  if (isMobile) return avatarEl;

  return <TooltipWrapper title={name}>{avatarEl}</TooltipWrapper>;
};

export default UserAvatar;
